import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import queryString from 'query-string'
import * as yup from 'yup'
import { ErrorResponse } from '@ocean/api'
import { usePrevious } from '@ocean/hooks'
import { Mode, Size } from '@ocean/types/dist/appearance'
import { layouts, common, controls, fields } from '@ocean/ui'
import { ErrorUtils } from 'utils'
import { GoToButton } from 'components/common'
import { AnimatedSection } from 'modules/Auth/components'
import { FinalState } from 'modules/Auth/components'
import { AuthFormLayout } from 'modules/Auth/layouts'
import { namespace } from 'modules/Auth/localization'
import authRoutes from 'modules/Auth/routing/routes'
import { SetupPasswordParams, SetupPasswordEmailLinkParams } from 'modules/Auth/types'
import { NoteWrapper, ErrorWrapper, BackButtonWrapper } from './SetupPasswordForm.styled'

const { Note, Icon } = common
const { Button, common: commonControls } = controls
const { LoadingLayout } = layouts
const { InputField } = fields
const { ErrorMessage } = commonControls

const I18_PATH = `${namespace}:auth.setupPassword`

type SetupPasswordFormProps = {
  onSubmit: (formData: SetupPasswordParams, emailLinkParams: SetupPasswordEmailLinkParams) => void
  loading?: boolean
  error?: ErrorResponse
  params?: queryString.ParsedQuery
}

const SetupPasswordForm: React.FC<SetupPasswordFormProps> = props => {
  const { onSubmit, loading, error, params } = props
  const prevLoading = usePrevious(loading)
  const [formikBag, setFormikBag] = useState<FormikHelpers<SetupPasswordParams> | null>(null)
  const [requestSuccessful, setSuccessState] = useState(false)

  useEffect(() => {
    if (prevLoading && !loading && formikBag) {
      formikBag.setSubmitting(false)
      if (!error) {
        setSuccessState(true)
      }
    }
  }, [formikBag, loading, prevLoading, error])

  const { t } = useTranslation()

  const VALIDATION_SCHEMA: yup.SchemaOf<SetupPasswordParams> = yup.object().shape({
    password: yup.string().required(t(`${I18_PATH}.passwordRequired`)),
    repeatPassword: yup
      .string()
      .oneOf([yup.ref('password'), undefined], t(`${I18_PATH}.passwordMatchError`))
      .required(t(`${I18_PATH}.repeatPasswordRequired`)),
  })

  const INITIAL_VALUES: SetupPasswordParams = {
    password: '',
    repeatPassword: '',
  }

  function handleSubmitForm(
    values: SetupPasswordParams,
    actions: FormikHelpers<SetupPasswordParams>
  ) {
    onSubmit(values, params as SetupPasswordEmailLinkParams)
    setFormikBag(actions)
  }

  function renderSuccess() {
    return (
      <FinalState
        title={t(`${I18_PATH}.header1`)}
        description={t(`${I18_PATH}.allSet`)}
        backText={t(`${I18_PATH}.login`)}
        backPath={authRoutes.login}
        dataTest='SetupPasswordSuccess'
      />
    )
  }

  function renderForm() {
    return (
      <>
        <AnimatedSection animationDuration='0.6s'>
          <NoteWrapper>
            <Note
              title={t(`${I18_PATH}.header1`)}
              description={t(`${I18_PATH}.header2`)}
              dataTest='SetupPasswordFormNote'
            />
          </NoteWrapper>
        </AnimatedSection>
        {error && (
          <ErrorWrapper>
            <ErrorMessage
              error={ErrorUtils.getPasswordErrorMessage(error, I18_PATH)}
              dataTest='SetupPasswordErrorMessage'
            />
          </ErrorWrapper>
        )}
        <Formik
          validationSchema={VALIDATION_SCHEMA}
          initialValues={INITIAL_VALUES}
          onSubmit={handleSubmitForm}
          validateOnMount
          validateOnBlur
          validateOnChange
        >
          {({ isValid, isSubmitting }) => (
            <LoadingLayout isLoading={loading}>
              <Form>
                <AuthFormLayout>
                  <AnimatedSection animationDuration='0.7s'>
                    <Field
                      title={t(`${I18_PATH}.password`)}
                      name='password'
                      type='password'
                      icon={<Icon icon='lock' />}
                      component={InputField}
                      size={Size.Large}
                    />
                  </AnimatedSection>
                  <AnimatedSection animationDuration='0.8s'>
                    <Field
                      title={t(`${I18_PATH}.repeatPassword`)}
                      name='repeatPassword'
                      type='password'
                      icon={<Icon icon='lock' />}
                      component={InputField}
                      size={Size.Large}
                    />
                  </AnimatedSection>
                  <AnimatedSection animationDuration='0.9s'>
                    <Button
                      fill
                      type='submit'
                      title={t(`${I18_PATH}.savePassword`)}
                      disabled={!isValid || isSubmitting}
                      dataTest='SetupPasswordSubmitButton'
                    />
                  </AnimatedSection>
                </AuthFormLayout>
              </Form>
            </LoadingLayout>
          )}
        </Formik>
      </>
    )
  }

  return (
    <AnimatedSection animationDuration='0.5s'>
      <BackButtonWrapper>
        <GoToButton
          mode={Mode.Dark}
          title={t(`${I18_PATH}.backToLogin`)}
          to={authRoutes.login}
          dataTest='SetupPasswordBackButton'
        />
      </BackButtonWrapper>
      {!requestSuccessful ? renderForm() : renderSuccess()}
    </AnimatedSection>
  )
}

export default SetupPasswordForm
